/**
 * Hook that spins up the force worker, feeds it the graph, and exposes the
 * latest position buffer via a ref.
 *
 * Layout strategy:
 *  1. Pin the protagonist (from config) at the origin. Defaults configured
 *     in `data/config.json`. Per-mode fallback if the primary id isn't
 *     present in the active graph (e.g., a position id in move-mode).
 *  2. Each node's target radius = R_BASE + weight * R_SPAN, where weight
 *     is the centrality-driven [0,1] effective importance (importance
 *     override if set, else normalized degree). Higher-importance nodes
 *     sit on the outer shell with even Fibonacci-sphere spacing; quieter
 *     nodes sit closer in.
 *  3. Top-K (config.hubCount) by weight are seeded onto a Fibonacci-sphere
 *     distribution at their target radius so the outer shell starts
 *     evenly populated.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  AppConfig,
  ClusterBy,
  Graph,
  GraphNode,
  Move,
  Position,
} from '../types/domain';
import { bezierControl, bezierSampleInto } from './bezier';
import { optimizeOnSphere, type OptNode } from './optimize';
import { clusterIdFor, subClusterIdFor } from '../data/cluster';
import { LAYOUT_SEED, seededRandom, spherePoint } from './rng';

export type ForceLayout = {
  posRef: React.MutableRefObject<Float32Array | null>;
  nodeIndex: Map<string, number>;
  ready: boolean;
};

// Sphere radii — stretched ~60% from the previous (5 / 16 / 7) values to
// give each node visual breathing room. Outer-shell hubs now sit at radius
// ~34 from origin instead of ~21.
//
// These are the "natural" radii for an unfiltered graph in either mode.
// When the active graph shrinks (cluster filter, difficulty filter,
// etc.), `radiusScale()` below tightens the shells in proportion so the
// constellation stays visually dense rather than getting lost in a
// fixed-size sphere. Surface-area scales as r², so we use sqrt(N) for
// the scale factor — keeps approximate node-per-area density constant.
const R_BASE = 8;
const R_SPAN = 26; // outer-shell distance = R_BASE + R_SPAN at weight = 1
const R_INTERCEPTION = 11;
const DEFAULT_HUB_COUNT = 8;

/**
 * Multiplier applied to R_BASE / R_SPAN / R_INTERCEPTION when the
 * graph is smaller than its "natural" size for the current mode.
 * Mode is inferred from the kind mix in simNodes — if any move kinds
 * are present we treat it as move-mode (where the unfiltered dataset
 * runs to a few hundred nodes); otherwise position-mode.
 *
 * Floors at 0.45 so a tiny filtered graph still has enough shell
 * separation between inner and outer hubs for the layout to settle
 * legibly.
 */
function radiusScale(simNodes: GraphNode[]): number {
  if (simNodes.length === 0) return 1;
  const hasMoves = simNodes.some((n) => n.kind === 'move');
  const baseline = hasMoves ? 250 : 60;
  const ratio = simNodes.length / baseline;
  const scale = Math.sqrt(Math.max(0.2, ratio));
  return Math.max(0.45, Math.min(1.25, scale));
}

function fibonacciSphere(n: number): Array<[number, number, number]> {
  if (n <= 0) return [];
  if (n === 1) return [[0, 1, 0]];
  const phi = Math.PI * (3 - Math.sqrt(5));
  const out: Array<[number, number, number]> = [];
  for (let i = 0; i < n; i++) {
    const y = 1 - (i / (n - 1)) * 2;
    const r = Math.sqrt(Math.max(0, 1 - y * y));
    const theta = phi * i;
    out.push([Math.cos(theta) * r, y, Math.sin(theta) * r]);
  }
  return out;
}

function targetRadiusFor(
  node: GraphNode,
  centerId: string,
  scale: number = 1,
): number {
  if (node.id === centerId) return 0;
  if (node.kind === 'interception') return R_INTERCEPTION * scale;
  return (
    R_BASE * scale +
    Math.max(0, Math.min(1, node.weight ?? 0)) * R_SPAN * scale
  );
}

export function pickCenter(graph: Graph, config?: AppConfig): string {
  const want = config?.centerNodeId;
  if (want && graph.byId.has(want)) return want;

  // Per-mode fallback from config
  const fb = config?.centerNodeFallback;
  if (fb) {
    const probe = (id: string | null | undefined) => id && graph.byId.has(id) ? id : null;
    const fromFallback =
      probe(fb.position as string | null | undefined) ??
      probe(fb.move as string | null | undefined);
    if (fromFallback) return fromFallback;
  }

  // Last resort: pick the heaviest node in the active graph.
  let best = graph.nodes[0]?.id ?? '';
  let bestW = -1;
  for (const n of graph.nodes) {
    if (n.weight > bestW) {
      bestW = n.weight;
      best = n.id;
    }
  }
  return best;
}

export function useForceLayout(
  graph: Graph,
  config?: AppConfig,
  clusterBy: ClusterBy = 'none',
  positionsById?: Map<string, Position>,
  movesById?: Map<string, Move>,
  // User-tunable multiplier (from the store / HUD slider) applied to
  // both the pre-sim and worker cluster pulls. 1.0 = baseline tuning.
  clusterStrength: number = 1,
  /**
   * Knot each family together inside its primary cluster. Off by default
   * — it is a second structure on top of the first, and turning it on
   * unasked would change every existing layout.
   */
  familySubClusters: boolean = false,
): ForceLayout {
  const posRef = useRef<Float32Array | null>(null);
  const [ready, setReady] = useState(false);
  // Derived from `graph` synchronously each render so consumers never see
  // a stale index against a freshly-allocated (and smaller) posRef. The
  // previous useState approach lagged by a render — when a HUD filter
  // shrank the graph, useFrame consumers in Nodes / Edges / ClusterLabels
  // would briefly index into pos[] using OLD ids mapped to OLD slots that
  // are out-of-bounds in the new buffer. The undefined reads produced
  // NaN matrices, which poisoned Three.js's bounding sphere / raycast
  // state and made the canvas stop accepting clicks until a reload.
  const nodeIndex = useMemo(() => {
    const idx = new Map<string, number>();
    const sim = graph.nodes.filter((n) => n.kind !== 'interception');
    const ints = graph.nodes.filter((n) => n.kind === 'interception');
    sim.forEach((n, i) => idx.set(n.id, i));
    ints.forEach((n, i) => idx.set(n.id, sim.length + i));
    return idx;
  }, [graph]);

  // Eagerly resize the position buffer in render so it lines up with
  // `nodeIndex` from this same render. The worker still owns
  // writing values into it — we just allocate the slots up-front so a
  // useFrame between renders doesn't see an index/buffer size mismatch.
  if (
    !posRef.current ||
    posRef.current.length !== graph.nodes.length * 3
  ) {
    posRef.current = new Float32Array(graph.nodes.length * 3);
  }

  useEffect(() => {
    const worker = new Worker(
      new URL('../workers/force.worker.ts', import.meta.url),
      { type: 'module' },
    );

    const centerId = pickCenter(graph, config);
    const hubCount = config?.hubCount ?? DEFAULT_HUB_COUNT;

    // Split: position/move nodes go to the sim; interception nodes are
    // pure derivatives positioned each tick at the bezier midpoint of
    // their parent move's arc (so the main move edge stays a single
    // unbroken curve). The index that maps a node id to its slot in
    // posRef is derived in the render-time useMemo above so consumers
    // never see a slot/index size mismatch during a graph swap.
    const simNodes = graph.nodes.filter((n) => n.kind !== 'interception');
    const interceptionNodes = graph.nodes.filter((n) => n.kind === 'interception');
    const index = nodeIndex;
    // Density-aware shell scaling so filtered graphs tighten up instead
    // of leaving the constellation lost in a fixed-size sphere.
    const rScale = radiusScale(simNodes);

    // Precompute per-interception data (parent move's from/to sim-indices
    // plus the t-value) so the per-tick loop is index-driven, no lookups.
    // Index every move (including non-default sleeve members) to its
    // sleeve endpoints. A sleeve's GraphEdge carries `moveIds` listing
    // ALL moves between (source, target); the default `moveId` is just
    // the first. Counters can hang off any move in the sleeve, so we
    // register every member here so the interception node finds the
    // sleeve's bezier endpoints regardless of which member it counters.
    const moveEndpoints = new Map<string, { fromId: string; toId: string }>();
    for (const e of graph.edges) {
      if (e.kind !== 'move') continue;
      if (e.moveIds && e.moveIds.length > 0) {
        for (const id of e.moveIds) {
          moveEndpoints.set(id, { fromId: e.source, toId: e.target });
        }
      } else if (e.moveId) {
        moveEndpoints.set(e.moveId, { fromId: e.source, toId: e.target });
      }
    }
    type IntInfo = { fromIdx: number; toIdx: number; t: number; outIdx: number };
    const interceptionInfo: IntInfo[] = [];
    interceptionNodes.forEach((n, i) => {
      if (!n.parentMoveId) return;
      const ep = moveEndpoints.get(n.parentMoveId);
      if (!ep) return;
      const fromIdx = index.get(ep.fromId);
      const toIdx = index.get(ep.toId);
      if (fromIdx === undefined || toIdx === undefined) return;
      interceptionInfo.push({
        fromIdx,
        toIdx,
        t: n.bezierT ?? 0.5,
        outIdx: simNodes.length + i,
      });
    });

    // Top-K hubs by effective weight (excluding the center)
    const hubCandidates = simNodes
      .filter((n) => n.id !== centerId && (n.kind === 'position' || n.kind === 'move'))
      .sort((a, b) => b.weight - a.weight)
      .slice(0, hubCount);
    const fibDirs = fibonacciSphere(hubCandidates.length);
    const hubSeed = new Map<string, [number, number, number]>();
    hubCandidates.forEach((n, i) => {
      const r = targetRadiusFor(n, centerId, rScale);
      const d = fibDirs[i];
      hubSeed.set(n.id, [d[0] * r, d[1] * r, d[2] * r]);
    });

    worker.onmessage = (ev: MessageEvent) => {
      const data = ev.data;
      if (data?.type !== 'positions') return;

      // Copy sim positions into the start of posRef.
      const wbuf = new Float32Array(data.buffer);
      if (!posRef.current || posRef.current.length !== graph.nodes.length * 3) {
        posRef.current = new Float32Array(graph.nodes.length * 3);
      }
      posRef.current.set(wbuf, 0);

      // For each interception, sample its parent move's bezier midpoint.
      const buf = posRef.current;
      for (const info of interceptionInfo) {
        const ax = buf[info.fromIdx * 3 + 0];
        const ay = buf[info.fromIdx * 3 + 1];
        const az = buf[info.fromIdx * 3 + 2];
        const bx = buf[info.toIdx * 3 + 0];
        const by = buf[info.toIdx * 3 + 1];
        const bz = buf[info.toIdx * 3 + 2];
        const { cx, cy, cz } = bezierControl(ax, ay, az, bx, by, bz);
        bezierSampleInto(
          ax, ay, az,
          cx, cy, cz,
          bx, by, bz,
          info.t,
          buf,
          info.outIdx * 3,
        );
      }

      if (!ready) setReady(true);
    };

    // --- Seed pass: Fibonacci for hubs, random jittered shell point for others. ---
    //
    // The generator is created HERE, per pass, not at module scope. This
    // effect re-runs on a graph change, a cluster-axis change or a filter
    // change, and a shared generator would continue the stream from
    // wherever the previous pass stopped — so switching cluster axis and
    // switching back would land on a different arrangement than the one
    // you left. Re-seeding per pass makes the layout a pure function of
    // its inputs, which is the property worth having. See rng.ts.
    const rand = seededRandom(LAYOUT_SEED);
    const optNodes: OptNode[] = simNodes.map((n) => {
      const r = targetRadiusFor(n, centerId, rScale);
      const seed = hubSeed.get(n.id);
      let x: number, y: number, z: number;
      if (n.id === centerId) {
        x = 0; y = 0; z = 0;
      } else if (seed) {
        x = seed[0]; y = seed[1]; z = seed[2];
      } else {
        // Draw order is part of the arrangement: the jitter takes one
        // value and the point two, so inserting a draw here — or
        // reordering these two lines — moves every subsequent node onto
        // a different sequence and reshuffles the whole map. Harmless,
        // but it will look like the change you were actually making.
        const jitter = 0.95 + rand() * 0.1;
        [x, y, z] = spherePoint(rand, r * jitter);
      }
      const clusterKey =
        clusterBy === 'none' || !positionsById || !movesById
          ? null
          : clusterIdFor(n, clusterBy, positionsById, movesById);
      const subKey =
        !familySubClusters || clusterBy === 'none' || !positionsById || !movesById
          ? null
          : subClusterIdFor(n, clusterBy, positionsById, movesById);
      return {
        id: n.id,
        x,
        y,
        z,
        pinned: n.id === centerId,
        targetRadius: r,
        cluster: clusterKey,
        subCluster: subKey,
      };
    });

    // --- Main-thread sphere-constrained optimizer: minimizes total edge
    // length subject to each node staying on its assigned shell. When
    // clusterBy is set, a same-cluster centroid pull is added so nodes
    // sharing the chosen attribute also group spatially.
    //
    // "Crown shyness" scaling: as the number of distinct clusters grows,
    // each cluster needs to push HARDER on its members to maintain a
    // distinct canopy in the canopy-share. Otherwise dense clusters
    // (saddle, x-guard) blur into their neighbors. Strength scales with
    // sqrt(clusterCount) so it ramps gently — 4 clusters ≈ 2× base,
    // 16 clusters ≈ 4× base.
    // ---
    const uniqueClusters = new Set<string>();
    if (clusterBy !== 'none') {
      for (const n of optNodes) if (n.cluster) uniqueClusters.add(n.cluster);
    }
    const clusterCount = uniqueClusters.size;
    const clusterScale = clusterCount > 0 ? Math.sqrt(clusterCount) : 0;
    const PRE_SIM_CLUSTER_BASE = 4.0;
    const WORKER_CLUSTER_BASE = 0.25;
    const preSimCluster =
      clusterBy === 'none'
        ? 0
        : PRE_SIM_CLUSTER_BASE * clusterScale * clusterStrength;
    const workerCluster =
      clusterBy === 'none'
        ? 0
        : WORKER_CLUSTER_BASE * clusterScale * clusterStrength;

    // Deliberately weaker than the primary. The sub level is a refinement
    // inside a region, not a competing organisation of the map — at equal
    // strength the two fight and the primary structure dissolves into a
    // scatter of family blobs. 0.6 keeps families legible as knots while
    // the coarse shape still reads first.
    const SUB_CLUSTER_FRACTION = 0.6;
    const preSimSub = familySubClusters ? preSimCluster * SUB_CLUSTER_FRACTION : 0;
    const workerSub = familySubClusters ? workerCluster * SUB_CLUSTER_FRACTION : 0;

    optimizeOnSphere(
      optNodes,
      graph.edges
        .filter((e) => {
          const s = graph.byId.get(e.source);
          const t = graph.byId.get(e.target);
          return s?.kind !== 'interception' && t?.kind !== 'interception';
        })
        .map((e) => ({ source: e.source, target: e.target, weight: e.weight })),
      { cluster: preSimCluster, subCluster: preSimSub },
    );

    worker.postMessage({
      type: 'init',
      centerNodeId: centerId,
      // Cluster force is scaled by sqrt(clusterCount) to preserve canopy
      // separation as the cluster count grows. See above.
      clusterStrength: workerCluster,
      subClusterStrength: workerSub,
      nodes: simNodes.map((n, i) => {
        const opt = optNodes[i];
        return {
          id: n.id,
          zone: n.zone,
          kind: n.kind,
          isHub: n.isHub,
          isOrphan: n.isOrphan,
          targetRadius: opt.targetRadius,
          pinned: opt.pinned,
          clusterId: opt.cluster,
          subClusterId: opt.subCluster,
          initialX: opt.x,
          initialY: opt.y,
          initialZ: opt.z,
        };
      }),
      // Only sim-to-sim links — counter-fork edges touch interception
      // nodes which the worker doesn't know about.
      links: graph.edges
        .filter((e) => {
          const s = graph.byId.get(e.source);
          const t = graph.byId.get(e.target);
          return s?.kind !== 'interception' && t?.kind !== 'interception';
        })
        .map((e) => ({
          source: e.source,
          target: e.target,
          weight: e.weight,
        })),
    });

    return () => {
      worker.postMessage({ type: 'stop' });
      worker.terminate();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph, config, clusterBy, positionsById, movesById, nodeIndex, clusterStrength, familySubClusters]);

  return { posRef, nodeIndex, ready };
}
